import { FormLayoutAppearance } from 'src/app/layout/form-layout/form-layout.types'
import { Stack, TextField } from '@vapor/material'
import { FormItem } from 'hic-components'
import { Controller } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ChargeCategoriesSingleSelect } from 'src/app/components/form-fields/charge-categories-single-select'
import HicAmountField from 'src/app/components/hic-amount-field/hic-amount-field'
import FormLayout from 'src/app/layout/form-layout/form-layout'
import { FormLayoutDefaultToolbar } from 'src/app/layout/form-layout/form-layout-toolbar'
import { ProductSchema, useAddNewProductHook } from './add-new-product.hook'
import { ChargeCategoryFragment } from 'src/api/api.generated'
import { useTenant } from 'src/app/hooks/context/context.hook'
import { DatePicker } from '@mui/x-date-pickers-pro'

export default function AddNewProductForm({
  appearance,
  onCancel,
  onSubmit,
  currentSelectedChargeCategory,
  propertyId,
  loading,
  checkin,
  checkout,
}: {
  appearance: FormLayoutAppearance
  onCancel: () => void
  onSubmit: (data: ProductSchema) => Promise<void>
  currentSelectedChargeCategory?: ChargeCategoryFragment | undefined
  propertyId: string
  loading?: boolean
  checkin?: Date | null
  checkout?: Date | null
}) {
  const { t } = useTranslation()
  const { currency } = useTenant()
  const { control, onFormSubmit, errors, setValue, formValue } = useAddNewProductHook({ onSubmit, currentSelectedChargeCategory, checkin: checkin ?? null, checkout: checkout ?? null })

  return (
    <FormLayout //
      title={t('pms.settings.product.form.title')}
      appearance={appearance}
      toolbar={
        <FormLayoutDefaultToolbar //
          saveButton={{
            clickHandler: onFormSubmit,
            text: t('pms.booking.pos.product-creation.create-product-title'),
            loading,
            disabled: loading,
          }}
          cancelButton={{
            clickHandler: onCancel,
            disabled: loading,
          }}
        />
      }
    >
      <Stack direction={'column'} spacing={1}>
        <Controller<ProductSchema, 'chargeCategoryId'>
          name={'chargeCategoryId'}
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem fullWidth fieldLabel={t('pms.settings.product.form.charge_category')} errorMessage={errors.chargeCategoryId?.message} sx={{ minWidth: 200 }}>
              <ChargeCategoriesSingleSelect
                value={value}
                onChange={onChange}
                onChangeChargeCategory={(value) => {
                  if (value) {
                    setValue('chargeCategory.defaultVat.value', value.defaultVat.value)
                    setValue('chargeCategory.defaultVat.code', value.defaultVat.code)
                    setValue('chargeCategory.name', value.name)
                    setValue('chargeCategory.defaultVatId', value.defaultVatId)
                  }
                }}
                error={errors.chargeCategoryId?.message}
                disabled={loading}
                propertyId={propertyId}
              />
            </FormItem>
          )}
        />
        <Controller<ProductSchema, 'description'>
          name={'description'}
          control={control}
          defaultValue={''}
          render={({ field: { value, onChange } }) => (
            <FormItem fullWidth fieldLabel={t('pms.settings.product.form.description')} errorMessage={errors.description?.message} sx={{ minWidth: 200 }}>
              <TextField disabled={loading} value={value} onChange={onChange} error={Boolean(errors.description?.message)} />
            </FormItem>
          )}
        />
        <Controller<ProductSchema, 'amount'>
          name={'amount'}
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem fullWidth fieldLabel={t('pms.settings.product.form.price')} errorMessage={errors.amount?.message}>
              <HicAmountField disabled={loading} currency={currency} value={value} onChange={onChange} error={Boolean(errors.amount?.message)} />
            </FormItem>
          )}
        />
        <FormItem fullWidth fieldLabel={t('pms.settings.product.form.vat')}>
          <TextField value={formValue?.chargeCategory?.defaultVat?.code ? formValue.chargeCategory.defaultVat.code : ''} disabled={true} />
        </FormItem>
        <Controller<ProductSchema, 'competenceDate'>
          name={'competenceDate'}
          control={control}
          render={({ field: { value, onChange } }) => (
            <FormItem fullWidth fieldLabel={t('pms.settings.product.form.competence')} errorMessage={errors.competenceDate?.message}>
              <DatePicker value={value ?? null} onChange={onChange} slotProps={{ textField: { variant: 'outlined' } }} minDate={checkin ?? undefined} maxDate={checkout ?? undefined} />
            </FormItem>
          )}
        />
      </Stack>
    </FormLayout>
  )
}
